import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as React from 'react';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { OrderPaymentType, OrderScenario } from 'components/order/model/Order';
import { Option } from 'lib/types';
import { isDefined, isUndefined } from 'lib/typeguards';
import {
    isSeparatedIntegerValid,
    parseIntOrNull,
    parseIntOrUndefined,
    stringOrNull as blankAsNull
} from 'lib/helpers';
import logger from 'lib/logger';
import { EGoodtillPriceSource } from './Goodtill/goodtill';
import { useSnackbar } from 'notistack';
import { EPointOfSaleProvider } from '../PointOfSaleSettings';
import { isDeliverectMenuServiceType } from './Deliverect/DeliverectSettingsEdit';
import { menusApi } from 'components/menu-manager/MenusApi';
import { MESSAGE_GENERAL_ERROR, MESSAGE_MENU_REFRESH } from 'config/messages';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { getPublicAndPrivateSettings } from 'store/settings/settingsSlice';
import { dictToEntries, entriesToDict } from 'lib/form/fields/DictionaryFormField';
import { toastGlobalValidationSchema, toastLocationValidationSchema } from './Toast/toastValidationSchema';

type IProps = {
    settings?: IPublicAndPrivateSettings;
    globalSettings: IPublicAndPrivateSettings;
    locationId?: string;
    onSettingsSaved: () => Promise<void>;
};

export const usePointOfSaleSettings = ({ settings, globalSettings, locationId, onSettingsSaved }: IProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const { claims } = useAppSelector(state => state.auth);

    const posProvider = React.useMemo(
        () => settings?.posProvider || globalSettings?.posProvider,
        [globalSettings, settings]
    );

    const initialValues = React.useMemo(() => {
        if (!settings) {
            return {
                posProvider
            };
        }
        return {
            posProvider,
            autoRefreshEnabled: settings.menu?.autoRefreshEnabled ?? true,
            manualRefreshEnabled: settings.menu?.manualRefreshEnabled ?? true,
            refreshSchedule: settings.menu?.refreshSchedule,
            autoRefreshAvailabilityEnabled: settings.menu?.autoRefreshAvailabilityEnabled ?? true,
            hiddenProductIds: settings.orderPosSync?.hiddenProductIds || [],

            /* OMNIVORE MICROS 3700 */
            omnivoreMicrosApiKey: settings.omnivore?.apiKey,
            omnivoreMicrosWebhookActivationCode: settings.omnivore?.webhookActivationCode,
            omnivoreMicrosLocationId: settings.omnivore?.locationId,
            omnivoreMicrosEmployeeId: settings.omnivore?.employeeId,
            omnivoreMicrosTenderTypeId: settings.omnivore?.tenderType,
            omnivoreMicrosPreOrderOrderTypeId: settings.omnivore?.PREORDER_orderType,
            omnivoreMicrosPreOrderRevenueCenterId: settings.omnivore?.PREORDER_revenueCenter,
            omnivoreMicrosTableOrderTypeId: settings.omnivore?.ORDER_TO_TABLE_orderType,
            omnivoreMicrosTableRevenueCenterId: settings.omnivore?.ORDER_TO_TABLE_revenueCenter,
            omnivoreMicrosPayAtTableOrderTypeId: settings.omnivore?.TABLE_orderType,
            omnivoreMicrosPayAtTableRevenueCenterId: settings.omnivore?.TABLE_revenueCenter,
            omnivoreMicrosMenuCategoryFilter: settings.omnivore?.menuCategoryFilter,
            omnivoreMicrosItemLevelPrice: settings.omnivore?.onlyUsePriceLevel,
            omnivoreMicrosItemMappingScheme: settings.omnivore?.itemMappingScheme,

            /* OMNIVORE ALOHA */
            omnivoreAlohaApiKey: settings.omnivore?.apiKey,
            omnivoreAlohaWebhookActivationCode: settings.omnivore?.webhookActivationCode,
            omnivoreAlohaLocationId: settings.omnivore?.locationId,
            omnivoreAlohaEmployeeId: settings.omnivore?.employeeId,
            omnivoreAlohaTenderTypeId: settings.omnivore?.tenderType,
            omnivoreAlohaPreOrderOrderTypeId: settings.omnivore?.PREORDER_orderType,
            omnivoreAlohaPreOrderCenterId: settings.omnivore?.PREORDER_revenueCenter,
            omnivoreAlohaOrderToTableOrderTypeId: settings.omnivore?.ORDER_TO_TABLE_orderType,
            omnivoreAlohaOrderToTableRevenueCenterId: settings.omnivore?.ORDER_TO_TABLE_revenueCenter,
            omnivoreAlohaPayAtTableOrderTypeId: settings.omnivore?.TABLE_orderType,
            omnivoreAlohaPayAtTableRevenueCenterId: settings.omnivore?.TABLE_revenueCenter,
            omnivoreAlohaMenuCategoryFilter: settings.omnivore?.menuCategoryFilter
                ? settings.omnivore?.menuCategoryFilter?.split(',')
                : [],
            omnivoreAlohaItemLevelPrice: settings.omnivore?.onlyUsePriceLevel?.split(',') || [],
            omnivoreAlohaManagerJobIds: settings.omnivore?.managerJobIds || [],
            omnivoreAlohaClerkJobIds: settings.omnivore?.clerkJobIds || [],
            omnivoreAlohaItemMappingScheme: settings.omnivore?.itemMappingScheme,
            omnivoreAlohaTerminalOrderFlagMenuItemId: settings.omnivore?.terminalOrderFlagMenuItemId,

            /* IBS ACCESS */
            ibsUseV2Api: settings.ibs?.useV2Api ?? false,
            ibsApiUsername: settings.ibs?.szUserName,
            ibsApiPassword: settings.ibs?.szPassword,
            ibsWebOrdersHostname: settings.ibs?.ibsWebOrdersHost,
            ibsAppGuid: settings.ibs?.szAppGUID,
            ibsAppGuidPassword: settings.ibs?.appGUIDPassword,
            ibsCompanyCode: settings.ibs?.szCompanyCode,
            ibsEnableKioskOrdersMode: settings.ibs?.useKioskOrder,
            ibsWebNotificationsEnabled: settings.ibs?.webNotificationsEnabled,
            ibsLocationId: settings.ibs?.ibsLocationId,
            menuIbsLocationId: settings.ibs?.menuIbsLocationId,
            ibsPaymentMediaType: settings.ibs?.paymentMediaType,
            ibsTerminalPaymentMediaType: settings.ibs?.TERMINAL_paymentMediaType,
            ibsTimezoneName: settings.ibs?.ibsPosIANATimeZone,
            ibsPreOrderSeatingArea: settings.ibs?.PREORDER_seatingArea,
            ibsOrderToTableSeatingArea: settings.ibs?.ORDER_TO_TABLE_seatingArea,
            ibsPayAtTableSeatingArea: settings.ibs?.TABLE_seatingArea,
            ibsTabSeatingArea: settings.ibs?.TAB_seatingArea,
            filterProductsByZone: !!settings.menu?.filterProductsByZone,
            ibsOrderNotesBold: settings.ibs?.orderNotesTextBold,
            ibsOrderNotesDoubleWidth: settings.ibs?.orderNotesTextDoubleWidth,
            ibsOrderNotesDoubleHeight: settings.ibs?.orderNotesTextDoubleHeight,
            ibsOrderNotesJustify: settings.ibs?.orderNotesTextJustify,
            PREORDER_priceLevel: settings.ibs?.PREORDER_priceLevel,
            ORDER_TO_TABLE_priceLevel: settings.ibs?.ORDER_TO_TABLE_priceLevel,
            TABLE_priceLevel: settings.ibs?.TABLE_priceLevel,
            TAB_priceLevel: settings.ibs?.TAB_priceLevel,
            condimentListOption: settings.ibs?.condimentListOption ?? '',
            ibsItemNoteModifierNo: settings.ibs?.itemNoteModifierNo,
            ibsItemNoteOffset: settings.ibs?.itemNoteOffset,
            ibsItemNoteSalesGroupNo: settings.ibs?.itemNoteSalesGroupNo,
            itemOrderNotesEnabled: settings.orderNotesEnabled?.item ?? false,

            /* ACCESS MENU MANAGER */
            accessMenuManagerEnabled: settings.accessMenuManagerEnabled,
            accessMenuManagerApiKey: settings.accessMenuManager?.apiKey,
            accessMenuManagerHost: settings.accessMenuManager?.host,
            accessMenuManagerPassword: settings.accessMenuManager?.password,
            preorderChannelId: settings.accessMenuManager?.PREORDER_channelId,
            orderToTableChannelId: settings.accessMenuManager?.ORDER_TO_TABLE_channelId,
            tabChannelId: settings.accessMenuManager?.TAB_channelId,
            tableChannelId: settings.accessMenuManager?.TABLE_channelId,

            /* REVEL */
            revelDiningOption: settings.revel?.diningOption,
            revelTaxInclusive: settings.revel?.taxinclusive,
            revelAccountName: settings.revel?.url,
            revelApiKey: settings.revel?.apiKey,
            revelPaymentType: settings.revel?.paymentType,
            revelEstablishmentId: settings.revel?.establishmentId,
            revelDynamicDiscounts: settings.revel?.useDynamicDiscounts,
            revelStockAvailability: settings.revel?.useStockAvailability,

            /* LIGHTSPEED L SERIES*/
            lightspeedWebOrdersServerHost: settings.lightspeed?.host,
            lightspeedLightspeedId: settings.lightspeed?.companyId,
            lightspeedPaymentTypeName: settings.lightspeed?.paymentTypeName,

            /* LIGHTSPEED K-SERIES */
            lightspeedKSeriesHost: settings.lightspeedKSeries?.host,
            lightspeedKSeriesLocationId: settings.lightspeedKSeries?.locationId,
            lightspeedKSeriesPaymentMethodCode: settings.lightspeedKSeries?.paymentMethodCode,

            /* COMTREX */
            comtrexVendorName: settings.comtrex?.vendorName,
            comtrexHost: settings.comtrex?.host,
            comtrexPaymentTypeId: settings.comtrex?.paymentTypeId,
            comtrexTerminalPaymentTypeId: settings.comtrex?.terminalPaymentTypeId,
            comtrexTipItemNumber: settings.comtrex?.tipItemNumber,
            comtrexNoteFireMessageId: settings.comtrex?.noteFireMessageId,
            comtrexAllergenListNumber: settings.comtrex?.allergenListNumber,
            comtrexMapAllergenAsCondiment: settings.comtrex?.mapAllergenAsCondiment ?? false,
            comtrexMenuCategoryFilter: settings.comtrex?.menuCategoryFilter?.split(',') ?? [],
            comtrexORDER_TO_TABLE_salesTypeNumber: settings.comtrex?.ORDER_TO_TABLE_salesTypeNumber,
            comtrexPREORDER_salesTypeNumber: settings.comtrex?.PREORDER_salesTypeNumber,
            comtrexTABLE_salesTypeNumber: settings.comtrex?.TABLE_salesTypeNumber,
            comtrexTAB_salesTypeNumber: settings.comtrex?.TAB_salesTypeNumber,
            comtrexLoyaltyAtPosBrandName: settings.comtrex?.loyaltyAtPosBrandName,
            comtrexProxyEnabled: settings.comtrex?.proxyEnabled ?? true,
            comtrexLoyaltyAtPosStoreId: settings.comtrex?.loyaltyAtPosStoreId,
            comtrexLoyaltyAtPosPassword: settings.comtrex?.loyaltyAtPosPassword,

            /* ZONAL AZTEC */
            zonalIsProduction: settings.zonalAztec?.isProduction ?? true,
            zonalAuthenticationBrandToken: settings.zonalAztec?.authBrandToken,
            zonalPlatformType: settings.zonalAztec?.platform,
            zonalBundleIdentifier: settings.zonalAztec?.bundleIdentifier,
            zonalHost: settings.zonalAztec?.host,
            zonalOrderToTableServiceId: settings.zonalAztec?.ORDER_TO_TABLE_serviceId,
            zonalPreOrderServiceId: settings.zonalAztec?.PREORDER_serviceId,
            zonalMenuId: settings.zonalAztec?.menuId,
            zonalNotesDisplayRecordId: settings.zonalAztec?.notesItemDisplayRecordId,
            zonalNotesItemIngredientId: settings.zonalAztec?.notesItemIngredientId,
            zonalNotesItemPortionTypeId: settings.zonalAztec?.notesItemPortionTypeId,
            zonalSalesAreaId: settings.zonalAztec?.salesAreaId,
            zonalSiteId: settings.zonalAztec?.siteId,
            tablePaymentMethodId: settings.zonalAztec?.tablePaymentMethodId,
            loyaltyAtPosPassword: settings.zonalAztec?.loyaltyAtPosPassword,
            chargeTaxIds: settings.zonalAztec?.chargeTaxIds?.join(', '),
            exclusiveTaxTaxIds: settings.zonalAztec?.exclusiveTaxTaxIds?.join(', '),
            discretionaryChargeTaxIds: settings.zonalAztec?.discretionaryChargeTaxIds?.join(', '),
            iOrderOpenDiscountsEnabled: settings.zonalAztec?.iOrderOpenDiscountsEnabled ?? false,
            zonalUseStdLoyalty: settings.zonalAztec?.useStdLoyalty ?? false,
            zonalDayRolloverTime: settings.zonalAztec?.dayRolloverTime,

            /* SQUARE */
            ...(settings.square && {
                squareIsProduction: settings.square?.isProduction ?? false,
                squareMerchantId: settings.square?.merchantId,
                squareLoyaltyProgramId: settings.square?.loyaltyProgramId,
                squareLocationId: settings.square?.locationId,
                squareWebhookHmacSecret: settings.square?.webhookHmacSecret,
                squareDiscountCodesEnabled: settings.square?.discountCodesEnabled ?? false
            }),
            userDeletion: {
                ...settings.userDeletion,
                deletePosAccountOnUserDeletion:
                    settings.userDeletion?.deletePosAccountOnUserDeletion === undefined
                        ? true
                        : settings.userDeletion?.deletePosAccountOnUserDeletion
            },

            /* DELIVERECT */
            mapTableNameToTableId: !!settings.deliverect?.mapTableNameToTableId,
            PREORDER_menuType: `${settings.deliverect?.PREORDER_menuType}`,
            ORDER_TO_TABLE_menuType: `${settings.deliverect?.ORDER_TO_TABLE_menuType}`,

            /* GOODTILL */
            goodtillSubdomain: settings.goodtill?.subdomain,
            goodtillUsername: settings.goodtill?.username,
            goodtillPassword: settings.goodtill?.password,
            goodtillVerificationToken: settings.goodtill?.verificationToken,
            goodtillLoyaltyAtPOSPerkId: settings.goodtill?.loyaltyAtPOSPerkId,
            goodtillOutletId: settings.goodtill?.outletId,
            goodtillPreorderPriceSource:
                settings.goodtill?.PREORDER_priceSource ?? EGoodtillPriceSource.DEFAULT,
            goodtillOrderToTablePriceSource:
                settings.goodtill?.ORDER_TO_TABLE_priceSource ?? EGoodtillPriceSource.DEFAULT,

            /* TEVALIS */
            ...(settings.tevalis && {
                tevalisCompanyId: settings.tevalis.companyId,
                tevalisDevId: settings.tevalis.devID,
                tevalisGuid: settings.tevalis.guid,
                tevalisGuid2: settings.tevalis.guid2,
                tevalisSiteId: settings.tevalis.siteId,
                tevalisPlaceholderItemPLU: settings.tevalis.placeholderItemPLU,
                tevalisPREORDER_priceGroupId: settings.tevalis.PREORDER_priceGroupId,
                tevalisORDER_TO_TABLE_priceGroupId: settings.tevalis.ORDER_TO_TABLE_priceGroupId,
                tevalisTABLE_priceGroupId: settings.tevalis.TABLE_priceGroupId,
                tevalisTAB_priceGroupId: settings.tevalis.TAB_priceGroupId,
                tevalisTenderType: settings.tevalis.tenderType,
                tevalisDiscountTenderType: settings.tevalis.discountTenderType,
                tevalisStockChangeWebhookEnabled: settings.tevalis.stockChangeWebhookEnabled ?? false
            }),

            /* MCR */
            ...(settings.mcr && {
                mcrCompanyGuid: settings.mcr?.companyGuid,
                mcrSharedSecret: settings.mcr?.sharedSecret,
                mcrAccountId: settings.mcr?.accountId,
                mcrLocationId: settings.mcr?.locationId
            }),

            /* SIMPHONY */
            ...(settings.simphony && {
                simphonyHost: settings?.simphony?.host,
                simphonyAuthHost: settings?.simphony?.authHost,
                simphonyClientId: settings?.simphony?.clientId,
                simphonyUsername: settings?.simphony?.username,
                simphonyPassword: settings?.simphony?.password,
                simphonyEnterpriseShortName: settings?.simphony?.enterpriseShortName,
                simphonyLocRef: settings?.simphony?.locRef,
                simphonyRvcRef: settings?.simphony?.rvcRef,
                simphonyCheckEmployeeRef: settings?.simphony?.checkEmployeeRef,
                simphonyDefaultPriceSequence: settings?.simphony?.defaultPriceSequence,
                simphonyPriceSequences: settings?.simphony?.priceSequences?.map(seq => ({
                    label: seq,
                    value: seq
                })),
                simphonyOrderTypeRef_DEFAULT: settings?.simphony?.orderTypeRef?.DEFAULT,
                simphonyOrderTypeRef_PREORDER: settings?.simphony?.orderTypeRef?.[OrderScenario.PREORDER],
                simphonyOrderTypeRef_OTT: settings?.simphony?.orderTypeRef?.[OrderScenario.ORDER_TO_TABLE],
                simphonyTenderId_DEFAULT: settings?.simphony?.tenderId?.DEFAULT,
                simphonyTenderId_CARD: settings?.simphony?.tenderId?.[OrderPaymentType.CARD_ON_FILE],
                simphonyTenderId_CASH: settings?.simphony?.tenderId?.[OrderPaymentType.CASH],
                simphonyTenderId_APPLEPAY: settings?.simphony?.tenderId?.[OrderPaymentType.APPLEPAY],
                simphonyTenderId_GOOGLEPAY: settings?.simphony?.tenderId?.[OrderPaymentType.GOOGLEPAY],
                simphonyTenderId_TERMINAL:
                    settings?.simphony?.tenderId?.[OrderPaymentType.CARDHOLDER_PRESENT],
                simphonyTenderId_GIFTCARD: settings?.simphony?.tenderId?.[OrderPaymentType.GIFT_CARD]
            }),

            /* QIKSERVE */
            ...(settings.qikserveConnect && {
                qikserveConnectHost: settings?.qikserveConnect?.host,
                qikserveConnectApiKey: settings?.qikserveConnect?.apiKey,
                qikserveConnectClientId: settings?.qikserveConnect?.clientId,
                qikserveConnectClientSecret: settings?.qikserveConnect?.clientSecret,
                qikserveConnectMarketName: settings?.qikserveConnect?.marketName,
                qikserveConnectLocale: settings?.qikserveConnect?.locale,
                qikserveConnectMenuOrderTypes: settings?.qikserveConnect?.menuOrderTypes,
                qikserveConnectStoreNumber: settings?.qikserveConnect?.storeNumber
            }),
            /* TOAST */
            ...(settings.toast && {
                toastHost: settings.toast?.host,
                toastClientId: settings.toast?.clientId,
                toastClientSecret: settings.toast?.clientSecret,
                toastRestaurantGuid: settings.toast?.restaurantGuid,
                toastPaymentTypeGuid: settings.toast?.paymentTypeGuid,
                toastScenarioToDiningOptionGuid: dictToEntries(settings.toast?.scenarioToDiningOptionGuid),
                toastScenarioToChargeGuid: dictToEntries(settings.toast?.scenarioToChargeGuid),
                toastScenarioToRevenueCenterGuid: dictToEntries(settings.toast?.scenarioToRevenueCenterGuid)
            })
        };
    }, [settings, posProvider]);

    const handleSubmitPosProvider = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};
                let menuSettingsToUpdate: IPublicAndPrivateSettings['menu'] = {};

                const hiddenProductIds = values.hiddenProductIds?.length > 0 ? values.hiddenProductIds : null;

                // Common Settings
                settingsToUpdate = {
                    ...settingsToUpdate,
                    posProvider: values.posProvider,
                    orderPosSync: {
                        ...settingsToUpdate.orderPosSync,
                        hiddenProductIds
                    }
                };

                if (settingsToUpdate.posProvider === 'SQUARE') {
                    settingsToUpdate.paymentProvider = 'SQUARE'; // Force Square
                }

                switch (values.posProvider) {
                    case EPointOfSaleProvider.OMNIVORE_MICROS_SIMPHONY:
                    case EPointOfSaleProvider.OMNIVORE_MICROS_3700: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                omnivore: {
                                    apiKey: values.omnivoreMicrosApiKey,
                                    webhookActivationCode: values.omnivoreMicrosWebhookActivationCode
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                omnivore: {
                                    locationId: values.omnivoreMicrosLocationId,
                                    employeeId: values.omnivoreMicrosEmployeeId,
                                    tenderType: values.omnivoreMicrosTenderTypeId,
                                    PREORDER_orderType: values.omnivoreMicrosPreOrderOrderTypeId,
                                    PREORDER_revenueCenter: values.omnivoreMicrosPreOrderRevenueCenterId,
                                    ORDER_TO_TABLE_orderType: values.omnivoreMicrosTableOrderTypeId,
                                    ORDER_TO_TABLE_revenueCenter: values.omnivoreMicrosTableRevenueCenterId,
                                    TABLE_orderType: values.omnivoreMicrosPayAtTableOrderTypeId,
                                    TABLE_revenueCenter: values.omnivoreMicrosPayAtTableRevenueCenterId,
                                    menuCategoryFilter: values.omnivoreMicrosMenuCategoryFilter,
                                    onlyUsePriceLevel: values.omnivoreMicrosItemLevelPrice,
                                    itemMappingScheme: values.omnivoreMicrosItemMappingScheme
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.OMNIVORE_NCR_ALOHA: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                omnivore: {
                                    apiKey: values.omnivoreAlohaApiKey,
                                    webhookActivationCode: values.omnivoreAlohaWebhookActivationCode
                                }
                            };
                        } else {
                            const menuCategoryFilter =
                                values.omnivoreAlohaMenuCategoryFilter?.length > 0
                                    ? values.omnivoreAlohaMenuCategoryFilter.join(',')
                                    : '';
                            const onlyUsePriceLevel =
                                values.omnivoreAlohaItemLevelPrice?.length > 0
                                    ? values.omnivoreAlohaItemLevelPrice.join(',')
                                    : '';
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                omnivore: {
                                    locationId: values.omnivoreAlohaLocationId,
                                    employeeId: values.omnivoreAlohaEmployeeId,
                                    tenderType: values.omnivoreAlohaTenderTypeId,
                                    PREORDER_orderType: values.omnivoreAlohaPreOrderOrderTypeId,
                                    PREORDER_revenueCenter: values.omnivoreAlohaPreOrderCenterId,
                                    ORDER_TO_TABLE_orderType: values.omnivoreAlohaOrderToTableOrderTypeId,
                                    ORDER_TO_TABLE_revenueCenter:
                                        values.omnivoreAlohaOrderToTableRevenueCenterId,
                                    TABLE_orderType: values.omnivoreAlohaPayAtTableOrderTypeId,
                                    TABLE_revenueCenter: values.omnivoreAlohaPayAtTableRevenueCenterId,
                                    menuCategoryFilter,
                                    onlyUsePriceLevel,
                                    clerkJobIds: values.omnivoreAlohaManagerJobIds?.length
                                        ? values.omnivoreAlohaClerkJobIds
                                        : null,
                                    managerJobIds: values.omnivoreAlohaManagerJobIds?.length
                                        ? values.omnivoreAlohaManagerJobIds
                                        : null,
                                    itemMappingScheme: values.omnivoreAlohaItemMappingScheme,
                                    terminalOrderFlagMenuItemId:
                                        values.omnivoreAlohaTerminalOrderFlagMenuItemId
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.IBS: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                ibs: {
                                    useV2Api: !!values.ibsUseV2Api,
                                    szUserName: values.ibsApiUsername,
                                    szPassword: values.ibsApiPassword,
                                    ibsWebOrdersHost: values.ibsWebOrdersHostname,
                                    szAppGUID: values.ibsAppGuid,
                                    appGUIDPassword: values.ibsAppGuidPassword,
                                    szCompanyCode: values.ibsCompanyCode,
                                    useKioskOrder: !!values.ibsEnableKioskOrdersMode,
                                    webNotificationsEnabled: !!values.ibsWebNotificationsEnabled,
                                    orderNotesTextBold: !!values.ibsOrderNotesBold,
                                    orderNotesTextDoubleHeight: !!values.ibsOrderNotesDoubleHeight,
                                    orderNotesTextDoubleWidth: !!values.ibsOrderNotesDoubleWidth,
                                    orderNotesTextJustify: parseIntOrUndefined(values.ibsOrderNotesJustify),
                                    PREORDER_priceLevel: parseIntOrNull(values.PREORDER_priceLevel),
                                    TABLE_priceLevel: parseIntOrNull(values.TABLE_priceLevel),
                                    TAB_priceLevel: parseIntOrNull(values.TAB_priceLevel),
                                    ORDER_TO_TABLE_priceLevel: parseIntOrNull(
                                        values.ORDER_TO_TABLE_priceLevel
                                    ),
                                    condimentListOption:
                                        values.condimentListOption === 'Default'
                                            ? null
                                            : blankAsNull(values.condimentListOption),
                                    itemNoteModifierNo: parseIntOrNull(values.ibsItemNoteModifierNo),
                                    itemNoteOffset: parseIntOrNull(values.ibsItemNoteOffset),
                                    itemNoteSalesGroupNo: parseIntOrNull(values.ibsItemNoteSalesGroupNo),
                                    paymentMediaType: values.ibsPaymentMediaType,
                                    TERMINAL_paymentMediaType: parseIntOrNull(
                                        values.ibsTerminalPaymentMediaType
                                    ),
                                    ibsPosIANATimeZone: values.ibsTimezoneName
                                },
                                orderNotesEnabled: {
                                    item: values.itemOrderNotesEnabled ?? false
                                },
                                accessMenuManagerEnabled: !!values.accessMenuManagerEnabled,
                                accessMenuManager: {
                                    host: values.accessMenuManagerHost,
                                    apiKey: values.accessMenuManagerApiKey,
                                    password: values.accessMenuManagerPassword,
                                    PREORDER_channelId: values.preorderChannelId,
                                    ORDER_TO_TABLE_channelId: values.orderToTableChannelId,
                                    TABLE_channelId: values.tableChannelId,
                                    TAB_channelId: values.tabChannelId
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                orderNotesEnabled: {
                                    item: values.itemOrderNotesEnabled ?? false
                                },
                                ibs: {
                                    useV2Api: !!values.ibsUseV2Api,
                                    ibsLocationId: values.ibsLocationId,
                                    menuIbsLocationId: values.menuIbsLocationId,
                                    paymentMediaType: values.ibsPaymentMediaType,
                                    TERMINAL_paymentMediaType: parseIntOrNull(
                                        values.ibsTerminalPaymentMediaType
                                    ),
                                    ibsPosIANATimeZone: values.ibsTimezoneName,
                                    PREORDER_seatingArea: parseIntOrUndefined(values.ibsPreOrderSeatingArea),
                                    ORDER_TO_TABLE_seatingArea: parseIntOrUndefined(
                                        values.ibsOrderToTableSeatingArea
                                    ),
                                    TABLE_seatingArea: parseIntOrUndefined(values.ibsPayAtTableSeatingArea),
                                    TAB_seatingArea: parseIntOrUndefined(values.ibsTabSeatingArea),
                                    PREORDER_priceLevel: parseIntOrNull(values.PREORDER_priceLevel),
                                    TABLE_priceLevel: parseIntOrNull(values.TABLE_priceLevel),
                                    TAB_priceLevel: parseIntOrNull(values.TAB_priceLevel),
                                    ORDER_TO_TABLE_priceLevel: parseIntOrNull(
                                        values.ORDER_TO_TABLE_priceLevel
                                    )
                                }
                            };

                            menuSettingsToUpdate = {
                                filterProductsByZone: values.filterProductsByZone
                            };
                            break;
                        }
                        break;
                    }
                    case EPointOfSaleProvider.REVEL: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                revel: {
                                    diningOption: values.revelDiningOption,
                                    taxinclusive: !!values.revelTaxInclusive,
                                    url: values.revelAccountName,
                                    apiKey: values.revelApiKey,
                                    useDynamicDiscounts: !!values.revelDynamicDiscounts,
                                    paymentType: parseIntOrUndefined(values.revelPaymentType),
                                    useStockAvailability: !!values.revelStockAvailability
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                revel: {
                                    establishmentId: values.revelEstablishmentId,
                                    useDynamicDiscounts: !!values.revelDynamicDiscounts,
                                    useStockAvailability: !!values.revelStockAvailability
                                }
                            };
                        }
                        break;
                    }
                    // this is L SERIES
                    case EPointOfSaleProvider.LIGHTSPEED: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                lightspeed: {
                                    host: values.lightspeedWebOrdersServerHost
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                lightspeed: {
                                    companyId: values.lightspeedLightspeedId,
                                    paymentTypeName: values.lightspeedPaymentTypeName
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.LIGHTSPEED_K_SERIES: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                lightspeedKSeries: {
                                    host: values.lightspeedKSeriesHost
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                lightspeedKSeries: {
                                    locationId: values.lightspeedKSeriesLocationId,
                                    paymentMethodCode: values.lightspeedKSeriesPaymentMethodCode
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.COMTREX: {
                        const menuCategoryFilter =
                            values.comtrexMenuCategoryFilter?.length > 0
                                ? values.comtrexMenuCategoryFilter.join(',')
                                : '';
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                comtrex: {
                                    vendorName: values.comtrexVendorName,
                                    loyaltyAtPosBrandName: values.comtrexLoyaltyAtPosBrandName,
                                    menuCategoryFilter,
                                    proxyEnabled: !!values.comtrexProxyEnabled
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                comtrex: {
                                    host: values.comtrexHost,
                                    paymentTypeId: values.comtrexPaymentTypeId,
                                    terminalPaymentTypeId: values.comtrexTerminalPaymentTypeId,
                                    tipItemNumber: values.comtrexTipItemNumber,
                                    noteFireMessageId: values.comtrexNoteFireMessageId,
                                    allergenListNumber: values.comtrexAllergenListNumber,
                                    mapAllergenAsCondiment: values.comtrexMapAllergenAsCondiment,
                                    menuCategoryFilter,
                                    ORDER_TO_TABLE_salesTypeNumber: blankAsNull(
                                        values.comtrexORDER_TO_TABLE_salesTypeNumber
                                    ),
                                    PREORDER_salesTypeNumber: blankAsNull(
                                        values.comtrexPREORDER_salesTypeNumber
                                    ),
                                    TABLE_salesTypeNumber: blankAsNull(values.comtrexTABLE_salesTypeNumber),
                                    TAB_salesTypeNumber: blankAsNull(values.comtrexTAB_salesTypeNumber),
                                    loyaltyAtPosStoreId: values.comtrexLoyaltyAtPosStoreId,
                                    loyaltyAtPosPassword: values.comtrexLoyaltyAtPosPassword
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.ZONAL_AZTEC: {
                        if (locationId === undefined) {
                            if (
                                (!!values.chargeTaxIds && !isSeparatedIntegerValid(values.chargeTaxIds)) ||
                                (!!values.exclusiveTaxTaxIds &&
                                    !isSeparatedIntegerValid(values.exclusiveTaxTaxIds)) ||
                                (!!values.discretionaryChargeTaxIds &&
                                    !isSeparatedIntegerValid(values.discretionaryChargeTaxIds))
                            ) {
                                enqueueSnackbar('Enter the tax ID and separate each ID with a comma', {
                                    variant: 'error'
                                });
                                return;
                            }
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                zonalAztec: {
                                    isProduction: values.zonalIsProduction,
                                    authBrandToken: values.zonalAuthenticationBrandToken,
                                    platform: values.zonalPlatformType,
                                    bundleIdentifier: values.zonalBundleIdentifier,
                                    host: values.zonalHost,
                                    integratorIdentity: values.integratorIdentity,
                                    hmacSharedSecret: values.hmacSharedSecret,
                                    chargeTaxIds: values.chargeTaxIds
                                        ?.split(',')
                                        ?.map((taxId: string) => Number(taxId.trim())),
                                    exclusiveTaxTaxIds: values.exclusiveTaxTaxIds
                                        ?.split(',')
                                        ?.map((taxId: string) => Number(taxId.trim())),
                                    discretionaryChargeTaxIds: values.discretionaryChargeTaxIds
                                        ?.split(',')
                                        ?.map((taxId: string) => Number(taxId.trim())),
                                    loyaltyAtPosPassword: values.loyaltyAtPosPassword,
                                    iOrderOpenDiscountsEnabled: values.iOrderOpenDiscountsEnabled,
                                    useStdLoyalty: values.zonalUseStdLoyalty,
                                    dayRolloverTime: values.zonalDayRolloverTime
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                zonalAztec: {
                                    ORDER_TO_TABLE_serviceId: parseIntOrUndefined(
                                        values.zonalOrderToTableServiceId
                                    ),
                                    PREORDER_serviceId: parseIntOrUndefined(values.zonalPreOrderServiceId),
                                    menuId: parseIntOrUndefined(values.zonalMenuId),
                                    notesItemDisplayRecordId: parseIntOrUndefined(
                                        values.zonalNotesDisplayRecordId
                                    ),
                                    notesItemIngredientId: parseIntOrUndefined(
                                        values.zonalNotesItemIngredientId
                                    ),
                                    notesItemPortionTypeId: parseIntOrUndefined(
                                        values.zonalNotesItemPortionTypeId
                                    ),
                                    salesAreaId: parseIntOrUndefined(values.zonalSalesAreaId),
                                    siteId: parseIntOrUndefined(values.zonalSiteId),
                                    tablePaymentMethodId: values.tablePaymentMethodId,
                                    loyaltyAtPosPassword: values.loyaltyAtPosPassword,
                                    iOrderOpenDiscountsEnabled: values.iOrderOpenDiscountsEnabled,
                                    dayRolloverTime: values.zonalDayRolloverTime
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.SQUARE: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                square: {
                                    isProduction: !!values.squareIsProduction,
                                    merchantId: values.squareMerchantId,
                                    loyaltyProgramId: values.squareLoyaltyProgramId,
                                    webhookHmacSecret: values.squareWebhookHmacSecret,
                                    discountCodesEnabled: !!values.squareDiscountCodesEnabled
                                },
                                userDeletion: {
                                    ...values.userDeletion,
                                    deletePosAccountOnUserDeletion:
                                        values.userDeletion.deletePosAccountOnUserDeletion
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                square: {
                                    locationId: values.squareLocationId
                                },
                                userDeletion: {
                                    ...values.userDeletion,
                                    deletePosAccountOnUserDeletion:
                                        values.userDeletion.deletePosAccountOnUserDeletion
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.DELIVERECT: {
                        const preorderMenuType = isDeliverectMenuServiceType(values.PREORDER_menuType)
                            ? values.PREORDER_menuType
                            : '';

                        const orderToTableMenuType = isDeliverectMenuServiceType(
                            values.ORDER_TO_TABLE_menuType
                        )
                            ? values.ORDER_TO_TABLE_menuType
                            : '';

                        settingsToUpdate = {
                            ...settingsToUpdate,
                            deliverect: {
                                ...settingsToUpdate.deliverect,
                                mapTableNameToTableId: values?.mapTableNameToTableId,
                                PREORDER_menuType: parseIntOrNull(preorderMenuType),
                                ORDER_TO_TABLE_menuType: parseIntOrNull(orderToTableMenuType)
                            }
                        };

                        break;
                    }
                    case EPointOfSaleProvider.GOODTILL: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                goodtill: {
                                    subdomain: values.goodtillSubdomain,
                                    username: values.goodtillUsername,
                                    password: values.goodtillPassword,
                                    verificationToken: values.goodtillVerificationToken,
                                    loyaltyAtPOSPerkId: values.goodtillLoyaltyAtPOSPerkId,
                                    PREORDER_priceSource: values.goodtillPreorderPriceSource,
                                    ORDER_TO_TABLE_priceSource: values.goodtillOrderToTablePriceSource
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                goodtill: {
                                    subdomain: values.goodtillSubdomain,
                                    username: values.goodtillUsername,
                                    password: values.goodtillPassword,
                                    outletId: values.goodtillOutletId,
                                    PREORDER_priceSource: values.goodtillPreorderPriceSource,
                                    ORDER_TO_TABLE_priceSource: values.goodtillOrderToTablePriceSource
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.TEVALIS: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                tevalis: {
                                    companyId: values.tevalisCompanyId,
                                    devID: values.tevalisDevId,
                                    guid: values.tevalisGuid,
                                    guid2: values.tevalisGuid2,
                                    placeholderItemPLU: values.tevalisPlaceholderItemPLU,
                                    stockChangeWebhookEnabled: values.tevalisStockChangeWebhookEnabled
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                tevalis: {
                                    siteId: parseIntOrNull(values.tevalisSiteId)
                                }
                            };
                        }
                        settingsToUpdate = {
                            ...settingsToUpdate,
                            tevalis: {
                                ...settingsToUpdate.tevalis,
                                PREORDER_priceGroupId: parseIntOrNull(values.tevalisPREORDER_priceGroupId),
                                ORDER_TO_TABLE_priceGroupId: parseIntOrNull(
                                    values.tevalisORDER_TO_TABLE_priceGroupId
                                ),
                                TABLE_priceGroupId: parseIntOrNull(values.tevalisTABLE_priceGroupId),
                                TAB_priceGroupId: parseIntOrNull(values.tevalisTAB_priceGroupId),
                                tenderType: values.tevalisTenderType,
                                discountTenderType: values.tevalisDiscountTenderType
                            }
                        };
                        break;
                    }
                    case EPointOfSaleProvider.MCR: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                mcr: {
                                    companyGuid: values.mcrCompanyGuid,
                                    sharedSecret: values.mcrSharedSecret,
                                    accountId: parseIntOrNull(values.mcrAccountId)
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                mcr: {
                                    locationId: values.mcrLocationId
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.SIMPHONY: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                simphony: {
                                    host: values.simphonyHost,
                                    authHost: values.simphonyAuthHost,
                                    clientId: values.simphonyClientId,
                                    username: values.simphonyUsername,
                                    password: values.simphonyPassword,
                                    enterpriseShortName: values.simphonyEnterpriseShortName
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                simphony: {
                                    locRef: values.simphonyLocRef,
                                    rvcRef: parseIntOrNull(values.simphonyRvcRef),
                                    checkEmployeeRef: parseIntOrNull(values.simphonyCheckEmployeeRef),
                                    defaultPriceSequence: parseIntOrNull(values.simphonyDefaultPriceSequence),
                                    priceSequences:
                                        values.simphonyPriceSequences?.map((option: Option) =>
                                            parseIntOrNull(option.value)
                                        ) ?? null,
                                    orderTypeRef: {
                                        DEFAULT: parseIntOrNull(values.simphonyOrderTypeRef_DEFAULT),
                                        [OrderScenario.PREORDER]: parseIntOrNull(
                                            values.simphonyOrderTypeRef_PREORDER
                                        ),
                                        [OrderScenario.ORDER_TO_TABLE]: parseIntOrNull(
                                            values.simphonyOrderTypeRef_OTT
                                        )
                                    },
                                    tenderId: {
                                        DEFAULT: parseIntOrNull(values.simphonyTenderId_DEFAULT),
                                        [OrderPaymentType.CARD_ON_FILE]: parseIntOrNull(
                                            values.simphonyTenderId_CARD
                                        ),
                                        [OrderPaymentType.CASH]: parseIntOrNull(values.simphonyTenderId_CASH),
                                        [OrderPaymentType.APPLEPAY]: parseIntOrNull(
                                            values.simphonyTenderId_APPLEPAY
                                        ),
                                        [OrderPaymentType.GOOGLEPAY]: parseIntOrNull(
                                            values.simphonyTenderId_GOOGLEPAY
                                        ),
                                        [OrderPaymentType.CARDHOLDER_PRESENT]: parseIntOrNull(
                                            values.simphonyTenderId_TERMINAL
                                        ),
                                        [OrderPaymentType.GIFT_CARD]: parseIntOrNull(
                                            values.simphonyTenderId_GIFTCARD
                                        )
                                    }
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.QIKSERVE: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                qikserveConnect: {
                                    host: values.qikserveConnectHost,
                                    apiKey: values.qikserveConnectApiKey,
                                    clientId: values.qikserveConnectClientId,
                                    clientSecret: values.qikserveConnectClientSecret,
                                    marketName: values.qikserveConnectMarketName,
                                    locale: values.qikserveConnectLocale,
                                    menuOrderTypes:
                                        isDefined(values.qikserveConnectMenuOrderTypes) &&
                                        values.qikserveConnectMenuOrderTypes.length > 0
                                            ? values.qikserveConnectMenuOrderTypes
                                            : null
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                qikserveConnect: {
                                    storeNumber: parseIntOrNull(values.qikserveConnectStoreNumber)
                                }
                            };
                        }
                        break;
                    }
                    case EPointOfSaleProvider.TOAST: {
                        if (locationId === undefined) {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                toast: {
                                    host: values.toastHost,
                                    clientId: values.toastClientId,
                                    clientSecret: values.toastClientSecret
                                }
                            };
                        } else {
                            settingsToUpdate = {
                                ...settingsToUpdate,
                                toast: {
                                    restaurantGuid: values.toastRestaurantGuid,
                                    paymentTypeGuid: values.toastPaymentTypeGuid,
                                    scenarioToDiningOptionGuid: entriesToDict(
                                        values.toastScenarioToDiningOptionGuid
                                    ),
                                    scenarioToChargeGuid: entriesToDict(values.toastScenarioToChargeGuid),
                                    scenarioToRevenueCenterGuid: entriesToDict(
                                        values.toastScenarioToRevenueCenterGuid
                                    )
                                }
                            };
                        }
                        break;
                    }
                }

                if (isUndefined(locationId)) {
                    menuSettingsToUpdate = {
                        ...menuSettingsToUpdate,
                        autoRefreshEnabled: values.autoRefreshEnabled,
                        manualRefreshEnabled: values.manualRefreshEnabled,
                        autoRefreshAvailabilityEnabled: values.autoRefreshAvailabilityEnabled
                    };
                }

                menuSettingsToUpdate = {
                    ...menuSettingsToUpdate,
                    refreshSchedule: values.refreshSchedule
                };

                if (!Object.keys(settingsToUpdate).length && !Object.keys(menuSettingsToUpdate).length) {
                    // No need to update anything
                    return true;
                }
                settingsToUpdate = {
                    ...settingsToUpdate,
                    menu: menuSettingsToUpdate
                };
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                if (!locationId) {
                    dispatch(getPublicAndPrivateSettings(claims));
                }
                if (locationId) {
                    menusApi.refreshMenus(locationId);
                    enqueueSnackbar(MESSAGE_MENU_REFRESH, { variant: 'info' });
                }
                await onSettingsSaved();
                return true;
            } catch (err) {
                enqueueSnackbar(MESSAGE_GENERAL_ERROR, { variant: 'error' });
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`, err);
                return false;
            }
        },
        [locationId, onSettingsSaved, dispatch, claims, enqueueSnackbar]
    );

    const validationSchema = React.useMemo(
        () => (locationId === undefined ? toastGlobalValidationSchema : toastLocationValidationSchema),
        [locationId]
    );

    return {
        initialValues,
        posProvider,
        handleSubmitPosProvider,
        validationSchema
    };
};
